import {
  type CrossClientQueryResultMatch,
  CrossClientQueryResolverStages,
  CrossClientQueryResultMatches,
  type CrossClientQueryExecution,
  type CrossClientQueryExecutionEvaluationCounts,
  type CrossClientQueryPhase,
  CrossClientQueryPhases,
  type CrossClientQueryResolverStage,
  type CrossClientQueryResultStatus,
  CrossClientQueryResultStatuses,
} from '../../models/CrossClient'

const hasRunningEvaluation = (counts?: CrossClientQueryExecutionEvaluationCounts | null): boolean => !!counts && (counts.running > 0 || counts.canceling > 0)

const hasQueuedEvaluation = (counts?: CrossClientQueryExecutionEvaluationCounts | null): boolean => !!counts && (counts.pending > 0 || counts.queued > 0)

export const executionHasRunningEvaluation = (execution?: CrossClientQueryExecution | null): boolean =>
  !!execution && hasRunningEvaluation(execution.evaluationCounts)

export const executionHasQueuedEvaluation = (execution?: CrossClientQueryExecution | null): boolean =>
  !!execution && !hasRunningEvaluation(execution.evaluationCounts) && hasQueuedEvaluation(execution.evaluationCounts)

// Initializing counts before the workflow's first step marks the run started.
export const bulkIsRunning = (execution: CrossClientQueryExecution): boolean =>
  (!!execution.bulkStartedAt || execution.phase === CrossClientQueryPhases.INITIALIZING) &&
  !execution.bulkFinishedAt &&
  !execution.finishedAt &&
  !execution.canceledAt

const RESULT_PHASES: CrossClientQueryPhase[] = [CrossClientQueryPhases.INITIALIZED, CrossClientQueryPhases.COMPLETED, CrossClientQueryPhases.CANCELED]

// Every other phase ends, or is still before, the point where the page can list results.
export const executionShowsResults = (execution: CrossClientQueryExecution): boolean => RESULT_PHASES.includes(execution.phase)

// Unknown (null) lists are not empty ones.
export const executionHasNothingResolvable = (execution: CrossClientQueryExecution): boolean =>
  execution.phase === CrossClientQueryPhases.INITIALIZED &&
  !bulkIsRunning(execution) &&
  execution.resolvableClauseVariables?.length === 0 &&
  execution.localResolvableClauseVariables?.length === 0

export const executionIsActive = (execution?: CrossClientQueryExecution | null): boolean =>
  !!execution && (bulkIsRunning(execution) || hasRunningEvaluation(execution.evaluationCounts) || hasQueuedEvaluation(execution.evaluationCounts))

interface ResolverProgress {
  status: CrossClientQueryResultStatus
  lastResolverStage?: CrossClientQueryResolverStage | null
  match?: CrossClientQueryResultMatch | null
}

// An unpaged client reads as unanswered; a review started now would be overwritten.
export const awaitsBulk = (execution: CrossClientQueryExecution | null | undefined, row: ResolverProgress): boolean =>
  !!execution && bulkIsRunning(execution) && !row.lastResolverStage && row.status === CrossClientQueryResultStatuses.INITIALIZED

// Mirrors the backend's eligible_next_steps.
export const owesLocalStage = (execution: CrossClientQueryExecution, match: CrossClientQueryResultMatch | null | undefined): boolean =>
  match === CrossClientQueryResultMatches.POTENTIAL ? execution.canLocalEvaluateTrue : execution.canLocalEvaluateTrue || execution.canLocalFulfillClause

export const needsLocalStage = (execution: CrossClientQueryExecution | null | undefined, row: ResolverProgress): boolean => {
  if (!execution || row.status !== CrossClientQueryResultStatuses.INITIALIZED) {
    return false
  }
  if (row.lastResolverStage === CrossClientQueryResolverStages.LOCAL) {
    return false
  }
  return owesLocalStage(execution, row.match)
}

// Owed local only while something can still run it.
export const awaitsResolvers = (execution: CrossClientQueryExecution | null | undefined, row: ResolverProgress, inFlight: boolean): boolean =>
  awaitsBulk(execution, row) || (inFlight && needsLocalStage(execution, row))
